import React, {useState} from 'react'
import {toast} from 'react-toastify'
import {CustomModal} from '@/app/modules/widgets/components/CustomModal'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {MembershipRole} from '@/shared/utils/SubscriptionProvider'
import {createRechargeCheckout, submitQuoteRequest} from './api'
import {
  ACCOUNT_CURRENCY,
  MAX_PACK_QUANTITY,
  RECHARGE_PACK_PRICE,
  RECHARGE_UNITS,
} from './catalog'
import {QuantityStepper} from './QuantityStepper'

interface RechargeDialogProps {
  closeModal: () => void
  role: MembershipRole
  subscriptionId: string | null
}

const newAttemptId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '')
  }
  return `attempt${Date.now().toString(36)}`
}

export const RechargeDialog: React.FC<RechargeDialogProps> = ({
  closeModal,
  role,
  subscriptionId,
}) => {
  const [quantity, setQuantity] = useState(1)
  const [isPaying, setIsPaying] = useState(false)
  const [isQuoting, setIsQuoting] = useState(false)
  const [quoteSubmitted, setQuoteSubmitted] = useState(false)

  const canPay = role === 'owner' || role === 'admin'
  const busy = isPaying || isQuoting
  const pack = RECHARGE_PACK_PRICE[ACCOUNT_CURRENCY]
  const totalLabel = `€${pack.amount * quantity}`

  const handleQuote = async () => {
    if (busy) return
    setIsQuoting(true)
    try {
      await submitQuoteRequest({kind: 'recharge', packs: quantity})
      setQuoteSubmitted(true)
    } catch (err: unknown) {
      const message =
        (err as {response?: {data?: {message?: string}}; message?: string})?.response?.data
          ?.message ||
        (err as {message?: string})?.message ||
        'Could not send the quote request'
      toast.error(message, toastDefaultConfig)
      getLogger().error('Recharge quote request failed:', err)
    } finally {
      setIsQuoting(false)
    }
  }

  const handlePay = async () => {
    if (!canPay || busy) return
    // Open synchronously on click so the browser does not treat it as a blocked pop-up
    // after the async checkout-session request.
    const checkoutWindow = window.open('about:blank', '_blank')
    if (!checkoutWindow) {
      toast.error(
        'Could not open Checkout. Allow pop-ups for this site, then try again.',
        toastDefaultConfig
      )
      return
    }
    setIsPaying(true)
    try {
      const session = await createRechargeCheckout(ACCOUNT_CURRENCY, quantity, newAttemptId())
      checkoutWindow.location.href = session.url
      toast.success('Checkout opened in a new tab.', toastDefaultConfig)
      closeModal()
    } catch (err: unknown) {
      checkoutWindow.close()
      const message =
        (err as {response?: {data?: {message?: string}}; message?: string})?.response?.data
          ?.message ||
        (err as {message?: string})?.message ||
        'Payment could not be started'
      toast.error(message, toastDefaultConfig)
      getLogger().error('Recharge checkout failed:', err)
    } finally {
      setIsPaying(false)
    }
  }

  if (quoteSubmitted) {
    return (
      <CustomModal
        title='Request received'
        titleIcon='fas fa-check-circle'
        closeModal={closeModal}
        buttons={[
          {
            text: 'Close',
            className: 'btn btn-primary',
            onClick: closeModal,
          },
        ]}
      >
        <p className='mb-0'>
          Thank you. Your request has been sent. Our sales team will follow up with a quote,
          including online payment and purchase-order options.
        </p>
      </CustomModal>
    )
  }

  return (
    <CustomModal
      title='Add units'
      titleIcon='fas fa-coins'
      closeModal={closeModal}
      buttons={[
        {
          text: 'Cancel',
          className: 'btn btn-light',
          onClick: closeModal,
          disabled: busy,
        },
        {
          text: isQuoting ? 'Sending…' : 'Request Quote',
          className: 'btn btn-outline-primary',
          onClick: () => void handleQuote(),
          disabled: busy || !subscriptionId,
        },
        {
          text: isPaying ? 'Opening…' : 'Checkout',
          className: 'btn btn-primary',
          onClick: () => void handlePay(),
          disabled: !canPay || busy || !subscriptionId,
        },
      ]}
    >
      <div>
        <p className='mb-3'>
          One pack adds <strong>{RECHARGE_UNITS.toLocaleString()}</strong> Cloud Units
          {subscriptionId ? (
            <>
              {' '}
              to subscription <strong>{subscriptionId}</strong>
            </>
          ) : null}
          . Units are applied after the payment is processed.
        </p>

        <div className='mb-3'>
          <QuantityStepper
            label='Packs'
            value={quantity}
            min={1}
            max={MAX_PACK_QUANTITY}
            disabled={busy}
            onChange={setQuantity}
            labelledById='recharge-quantity-label'
          />
          <div className='form-text'>
            {quantity.toLocaleString()} × {RECHARGE_UNITS.toLocaleString()} ={' '}
            {(quantity * RECHARGE_UNITS).toLocaleString()} units · {totalLabel}
          </div>
        </div>

        {!canPay && (
          <p className='text-muted mb-0'>
            Ask an owner or admin of this subscription to pay by card. You can still request a
            quote.
          </p>
        )}
      </div>
    </CustomModal>
  )
}
